/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { momentWithUserTimezone } from 'js/utils/DateTimeUtils';

import { InlineNotification, TextField, Typography2 } from '@coursera/cds-core';

import { Spacer } from 'bundles/enterprise-ui/components';
import type { DateRangeValue } from 'bundles/enterprise-ui/components/DateRangeInput';
import { DateRangeInput } from 'bundles/enterprise-ui/components/DateRangeInput';

import _t from 'i18n!nls/admin-dashboard';

type SessionWithDateInputProps = {
  date?: DateRangeValue;
  name: string;
  disabled?: boolean;
  error?: string | React.ReactNode;
  maxEndDate?: number;
  onChangeName: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeDate: (date: DateRangeValue) => void;
};

const SessionWithDateInput: React.FC<SessionWithDateInputProps> = ({
  date,
  disabled,
  name,
  error,
  maxEndDate,
  onChangeName,
  onChangeDate,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Hack to fix:
  // TextField doesn't seem to focus on click when placed inside RadioCard.
  React.useEffect(() => {
    const element = inputRef.current;

    if (!element) return;

    element.addEventListener('click', () => {
      element.focus();
    });
  });

  return (
    <React.Fragment>
      <DateRangeInput
        disabled={disabled}
        value={date}
        onChange={onChangeDate}
        appendToBody={false}
        forceUserTimeZone={true}
        limit={
          maxEndDate
            ? {
                startDate: momentWithUserTimezone().add(1, 'd').format(),
                endDate: momentWithUserTimezone(maxEndDate).format(),
              }
            : {
                startDate: momentWithUserTimezone().add(1, 'd').format(),
              }
        }
      />
      <Spacer size={24} />
      {error && (
        <React.Fragment>
          <InlineNotification severity="error">
            <Typography2 component="p">{error}</Typography2>
          </InlineNotification>
          <Spacer size={24} />
        </React.Fragment>
      )}
      <TextField
        label={_t('Name this availability period (optional)')}
        value={name}
        placeholder={_t('e.g. Spring Term')}
        onChange={onChangeName}
        data-testid="session-name"
        maxLength={35}
        inputRef={inputRef}
        className="availability-textfield"
      />
    </React.Fragment>
  );
};

export default SessionWithDateInput;
